import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  aggregateParts,
  isReviewStatus,
  jobDisplayName,
  jobKey,
  jobToBomCard,
  mapJobItemsToBom,
  mergeJobLists,
  sortCatalog,
  trustedListItemCount,
  withHydratedItemCount,
  type CatalogJobInput,
  type CatalogPartCard,
  type CatalogPartLine,
} from "../lib/catalogModel";
import { mapPool } from "../lib/catalogThumbCache";
import { buildE2eHitlJob, isE2eMockMode } from "../lib/e2eFixture";
import { getJob, listJobs, type JobListItem } from "../lib/jobApi";
import type { BomItem, ForecastState } from "../types";

/** Pace detail fetches. Every listed job is loaded so its line count can match the detail page. */
const JOB_FETCH_CONCURRENCY = 2;
/** How many not-yet-shown sorted part cards to append per tick. */
const REVEAL_CHUNK = 2;
/** Pause between UI publishes so the browser can paint. */
const REVEAL_MS = 90;

function jobsToList(jobs: JobListItem[]): CatalogJobInput[] {
  return jobs
    .filter((j) => typeof j?.job_id === "string" && j.job_id)
    .map((j) => {
      const label = j.file_name || j.drawing_id || j.job_id;
      const count = trustedListItemCount(j.items);
      return {
        drawing_id: j.job_id,
        job_id: j.job_id,
        file_path: label || undefined,
        name: label || undefined,
        status: j.status,
        ...(count == null ? {} : { items: count, itemCountKnown: true }),
        created_at: j.created_at ?? null,
        updated_at: j.updated_at ?? null,
        has_thumbnail: Boolean(j.has_thumbnail || j.thumbnail_url),
        thumbnail_url: j.thumbnail_url ?? null,
      };
    });
}

function forecastToJob(f: ForecastState): CatalogJobInput {
  return {
    drawing_id: f.job_id || f.drawing_id,
    job_id: f.job_id || f.drawing_id,
    name: f.document_name,
    file_path: f.document_name,
    status: f.status,
    items: f.items.length,
    itemCountKnown: true,
    created_at: null,
    updated_at: null,
  };
}

/** BOM rows already on the job (demo fixture). A numeric `items` count is not rows. */
function embeddedBomItems(job: CatalogJobInput): BomItem[] | null {
  if (!Array.isArray(job.items) || job.items.length === 0) return null;
  const first = job.items[0];
  if (!first || typeof first !== "object") return null;
  const row = first as { item_no?: unknown; part_no?: unknown };
  if (row.part_no == null && row.item_no == null) return null;
  return job.items as BomItem[];
}

function linesForJob(
  job: CatalogJobInput,
  items: BomItem[],
): CatalogPartLine[] {
  const id = jobKey(job);
  if (!id) return [];
  return items.map((item) => ({
    item,
    parentJobId: id,
    parentName: jobDisplayName(job),
    parentUpdatedAt: job.updated_at || job.created_at || null,
  }));
}

export function useOrgCatalog(
  recentFiles: CatalogJobInput[] = [],
  forecasts: ForecastState[] = [],
) {
  const [serverJobs, setServerJobs] = useState<CatalogJobInput[]>([]);
  /** Line counts from GET /v1/jobs/{id} `items`, keyed by job id. */
  const [hydratedCounts, setHydratedCounts] = useState<Record<string, number>>({});
  /** Full part-line buffer — grows as each job fetch completes. */
  const [partLines, setPartLines] = useState<CatalogPartLine[]>([]);
  /**
   * Append-only reveal order. New cards are taken from the current date-sorted
   * list among *not yet shown* ids, then appended — already-visible indices never move.
   */
  const [shownPartIds, setShownPartIds] = useState<string[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [partsLoading, setPartsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const partsLoadGen = useRef(0);
  const rowsRef = useRef<CatalogJobInput[]>([]);
  const partLinesRef = useRef<CatalogPartLine[]>([]);
  const shownPartIdsRef = useRef<string[]>([]);

  const rows = useMemo(() => {
    const fromForecasts = forecasts.map(forecastToJob);
    return mergeJobLists(mergeJobLists(serverJobs, recentFiles), fromForecasts);
  }, [serverJobs, recentFiles, forecasts]);
  rowsRef.current = rows;

  const loadParts = useCallback(
    async (jobs: CatalogJobInput[], opts?: { reset?: boolean }) => {
      const gen = ++partsLoadGen.current;
      setPartsLoading(true);
      if (opts?.reset) {
        partLinesRef.current = [];
        shownPartIdsRef.current = [];
        setPartLines([]);
        setShownPartIds([]);
      }

      let fetchDone = false;

      const mergeJobLines = (id: string, lines: CatalogPartLine[]) => {
        if (gen !== partsLoadGen.current) return;
        const without = partLinesRef.current.filter((l) => l.parentJobId !== id);
        partLinesRef.current = without.concat(lines);
        setPartLines(partLinesRef.current);
      };

      /** Append next sorted newcomers without reshuffling already-shown cards. */
      const appendRevealChunk = (): boolean => {
        if (gen !== partsLoadGen.current) return false;
        const sorted = sortCatalog(
          aggregateParts(partLinesRef.current),
        ) as CatalogPartCard[];
        const have = new Set(shownPartIdsRef.current);
        const add = sorted
          .filter((c) => !have.has(c.id))
          .slice(0, REVEAL_CHUNK)
          .map((c) => c.id);
        if (add.length === 0) return false;
        shownPartIdsRef.current = shownPartIdsRef.current.concat(add);
        setShownPartIds(shownPartIdsRef.current);
        return true;
      };

      const revealLoop = (async () => {
        while (gen === partsLoadGen.current) {
          const added = appendRevealChunk();
          if (!added) {
            if (fetchDone) break;
            await new Promise<void>((r) => setTimeout(r, REVEAL_MS));
            continue;
          }
          await new Promise<void>((resolve) => {
            requestAnimationFrame(() => {
              setTimeout(resolve, REVEAL_MS);
            });
          });
        }
        if (gen === partsLoadGen.current) {
          // Flush any remainder in stable append order.
          while (appendRevealChunk()) {
            /* drain */
          }
          setPartsLoading(false);
        }
      })();

      await mapPool(jobs, JOB_FETCH_CONCURRENCY, async (job) => {
        if (gen !== partsLoadGen.current) return;
        const id = jobKey(job);
        if (!id) return;
        const forecast = forecasts.find(
          (f) =>
            f.job_id === id ||
            f.drawing_id === job.drawing_id ||
            f.drawing_id === id,
        );
        const embedded = embeddedBomItems(job);
        let items: BomItem[] = forecast?.items?.length
          ? forecast.items.map((it) => ({
              ...it,
              // Only a URL the engine stored. thumbnail_part_{n} 404s.
              thumbnail_url: it.thumbnail_url ?? null,
            }))
          : embedded
            ? embedded.map((it) => ({
                ...it,
                thumbnail_url: it.thumbnail_url ?? null,
              }))
            : [];
        if (items.length === 0 && Array.isArray(job.items)) {
          // Demo / fixture rows already carry the lines the detail page counts.
          items = mapJobItemsToBom(job.items, id);
        }
        // A numeric list count (often the engine's default 0) is not the BOM.
        if (items.length === 0 && !Array.isArray(job.items)) {
          try {
            const rec = await getJob(id);
            if (gen !== partsLoadGen.current) return;
            // Same array the detail page counts. The list payload's items: 0 is not this.
            if (Array.isArray(rec.items)) {
              const count = rec.items.length;
              setHydratedCounts((prev) =>
                prev[id] === count ? prev : { ...prev, [id]: count },
              );
            }
            items = mapJobItemsToBom(rec.items, id);
          } catch {
            items = [];
          }
        }
        if (gen !== partsLoadGen.current) return;
        mergeJobLines(id, linesForJob(job, items));
      });

      fetchDone = true;
      await revealLoop;
    },
    [forecasts],
  );

  const refresh = useCallback(async () => {
    setJobsLoading(true);
    setError(null);
    setHydratedCounts({});
    const local = mergeJobLists(recentFiles, forecasts.map(forecastToJob));
    if (isE2eMockMode()) {
      // Drawings passes its own demo catalog (thumbs + priced lines). Home does
      // not, so seed the assembly the detail page counts instead of listJobs.
      const seeded: CatalogJobInput[] =
        recentFiles.length > 0
          ? []
          : (() => {
              const fixture = buildE2eHitlJob();
              return [
                {
                  drawing_id: fixture.drawing_id || fixture.job_id,
                  job_id: fixture.job_id,
                  file_path: fixture.master_path,
                  name: fixture.master_path,
                  status: fixture.status,
                  items: fixture.items,
                  created_at: null,
                  updated_at: null,
                },
              ];
            })();
      setServerJobs(seeded);
      setJobsLoading(false);
      await loadParts(mergeJobLists(seeded, local), { reset: true });
      return;
    }
    try {
      const jobs = await listJobs(50);
      const list = jobsToList(Array.isArray(jobs) ? jobs : []);
      setServerJobs(list);
      setJobsLoading(false);
      await loadParts(mergeJobLists(list, local), { reset: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setServerJobs([]);
      setJobsLoading(false);
      await loadParts(local, { reset: true });
    }
  }, [forecasts, loadParts, recentFiles]);

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boot once; refresh() is user-triggered after
  }, []);

  // Only re-expand parts when forecasts change — not on every rows update (avoids double fetch).
  useEffect(() => {
    if (forecasts.length === 0) return;
    void loadParts(rowsRef.current);
  }, [forecasts, loadParts]);

  const boms = useMemo(
    () =>
      rows.map((job) =>
        withHydratedItemCount(jobToBomCard(job), hydratedCounts[jobKey(job)]),
      ),
    [hydratedCounts, rows],
  );
  const allParts = useMemo(
    () => sortCatalog(aggregateParts(partLines)) as CatalogPartCard[],
    [partLines],
  );
  /** Stable display order: append-only ids, live card data from the latest aggregate. */
  const parts = useMemo(() => {
    const byId = new Map<string, CatalogPartCard>(
      allParts.map((c) => [c.id, c]),
    );
    return shownPartIds
      .map((id) => byId.get(id))
      .filter((c): c is CatalogPartCard => c != null);
  }, [allParts, shownPartIds]);
  const cards = useMemo(
    () => [...sortCatalog(boms), ...parts],
    [boms, parts],
  );
  const reviewBoms = useMemo(
    () => boms.filter((b) => isReviewStatus(b.status)),
    [boms],
  );

  const loading = jobsLoading || partsLoading;

  return {
    rows,
    boms,
    parts,
    cards,
    reviewBoms,
    loading,
    jobsLoading,
    partsLoading,
    error,
    refresh,
    setServerJobs,
  };
}
